import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { OccupationSeries } from '@gfe/features/components/visualization/occupation/models/occupation.model';
import { OccupationChartComponent } from '@gfe/features/components/visualization/occupation/occupation-chart/occupation-chart.component';
import { OccupationFormService } from '@gfe/features/components/visualization/occupation/occupation-form.service';
import { OccupationLegendComponent } from '@gfe/features/components/visualization/occupation/occupation-legend/occupation-legend.component';

import { CommentV4 } from '@core/models/comments-v4.model';
import { ICONS } from '@core/services/icons/icons.service';
import { ModifierKeysPressedService } from '@core/services/modifier-keys-pressed/modifier-keys-pressed.service';
import { NotificationService } from '@core/services/notification/notification.service';
import { getFormatedTimeFromDate } from '@core/utils/date-utils';
import { formatDuration } from '@core/utils/time-utils';

import { ButtonComponent } from '@shared/components/button/button.component';
import { TimeAgoPipe } from '@shared/pipes/time-ago.pipe';

/** A translated, display-ready task label (decoupled from the visualization VisuItemLabel). */
export interface StepLabel {
  label: string;
  value: string;
}

/** Minimal step shape the Details pane needs (structural, decoupled from Task). */
export interface TimelineStepInfo {
  name: string;
  kind: string;
  startDate: Date;
  endDate: Date;
  siteUid?: string;
  /** 0-based sequential step number within the route, shown as the step number. */
  actionIndex?: number;
  actionUid?: string;
  /** Translated task infos, shown as extra rows in the Details tab. */
  labels?: StepLabel[];
}

type PopoverTab = 'comments' | 'details' | 'occupation';

/**
 * Interactive popover showing a step's comment conversation (with author,
 * timestamps, per-comment edit/delete, conversation-level resolve) plus a
 * details tab. Dumb component: persistence happens in the parent.
 */
@Component({
  selector: 'app-step-comments-popover',
  imports: [
    FormsModule,
    TranslatePipe,
    DatePipe,
    ButtonComponent,
    TimeAgoPipe,
    OccupationChartComponent,
    OccupationLegendComponent
  ],
  templateUrl: './step-comments-popover.component.html',
  // The host dissolves into its parent flex layout so the root below fills the
  // floating wrapper's capped height and can scroll its middle region.
  styles: `
    :host {
      display: contents;
    }
  `
})
export class StepCommentsPopoverComponent {
  private readonly translate = inject(TranslateService);
  private readonly notificationService = inject(NotificationService);
  private readonly modifierKeysService = inject(ModifierKeysPressedService);
  private readonly occupationFormService = inject(OccupationFormService);
  protected readonly ICONS = ICONS;
  protected readonly isMetaPressed = this.modifierKeysService.isMetaPressed;

  readonly title = input<string>('');
  readonly comments = input<CommentV4[]>([]);
  readonly resolved = input<boolean>(false);
  readonly loading = input<boolean>(false);
  readonly disabled = input<boolean>(false);
  /**
   * True when the task does not support comments (e.g. non-work steps): the comments
   * tab is hidden and the popover defaults to details. Unlike `disabled` (the transient
   * mutation lock used while saving), this is a permanent property of the task.
   */
  readonly commentsDisabled = input<boolean>(false);
  readonly task = input<TimelineStepInfo | null>(null);
  readonly occupationSeries = input<OccupationSeries[]>([]);
  readonly occupationLoading = input<boolean>(false);
  /** Interval to outline on the chart; the parent knows what this step actually holds. */
  readonly occupationMarkWindowMs = input<[number, number] | null>(null);
  /** False when this stop holds nothing here, so the chart below is the site's, not its. */
  readonly stopoverHoldsInfrastructure = input<boolean>(true);

  readonly add = output<string>();
  readonly editComment = output<{ id: string; message: string }>();
  readonly removeComment = output<string>();
  readonly toggleResolve = output<void>();
  readonly dismiss = output<void>();
  /** Raised when the reader opens the tab, so the parent can fetch what it will show. */
  readonly occupationOpened = output<void>();

  protected readonly activeTab = signal<PopoverTab>('details');
  protected readonly draft = signal('');
  protected readonly currentLang = computed(() => this.translate.getCurrentLang());
  protected readonly canSubmit = computed(() => this.draft().trim().length > 0);

  /** Id of the comment currently being edited, or null. */
  protected readonly editingId = signal<string | null>(null);
  protected readonly editDraft = signal('');

  protected readonly formatedDuration = computed(() => {
    const t = this.task();
    if (!t) return '0s';
    return formatDuration({
      totalMilliseconds: t.endDate.getTime() - t.startDate.getTime()
    });
  });

  protected readonly kindKey = computed(
    () => `visualization.timeline.step_detail.kind_${this.task()?.kind ?? 'work'}`
  );

  /**
   * A step with a site has occupation to show; whether any has been fetched yet is not the
   * question. Gating on loaded series would hide the tab that triggers the fetch.
   */
  protected readonly hasOccupation = computed(() => this.task()?.siteUid != null);
  protected readonly occupationForm = this.occupationFormService.formFor('popup');

  constructor() {
    effect(() => {
      const disabled = this.commentsDisabled();
      if (disabled) this.setTab('details');
    });

    // One popover instance serves every step, so the tab outlives the step that opened
    // it. Left alone, a step with nothing to chart keeps the occupation pane open with
    // its tab button gone — a blank body under a tab bar with nothing selected.
    effect(() => {
      if (!this.hasOccupation() && this.activeTab() === 'occupation') {
        this.setTab('details');
      }
    });

    // Asked for whenever the pane is showing a step, not only when the tab is clicked.
    // The tab outlives the step, so opening a second step with occupation already active
    // is a new site to fetch and no click to trigger it — the pane sat empty until the
    // reader clicked away and back.
    // Reads the task rather than `hasOccupation`, which stays `true` from one site to the
    // next and so would not re-run the effect for the step that needs the new fetch.
    effect(() => {
      const task = this.task();
      if (this.activeTab() === 'occupation' && task?.siteUid != null) {
        this.occupationOpened.emit();
      }
    });
  }

  protected setTab(tab: PopoverTab): void {
    this.activeTab.set(tab);
  }

  protected cycleOccupationForm(): void {
    this.occupationFormService.cycle('popup');
  }

  protected onInput(event: Event): void {
    this.draft.set((event.target as HTMLInputElement).value);
  }

  protected onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.submit();
    }
    // Shift+Enter is allowed to insert a newline in the textarea — do nothing
  }

  protected submit(): void {
    if (this.disabled()) return;
    const text = this.draft().trim();
    if (!text) return;
    this.add.emit(text);
    this.draft.set('');
  }

  protected onRemove(id: string): void {
    if (this.disabled()) return;
    this.removeComment.emit(id);
  }

  protected onToggleResolve(): void {
    if (this.disabled()) return;
    this.toggleResolve.emit();
  }

  protected startEdit(comment: CommentV4): void {
    this.editingId.set(comment.id);
    this.editDraft.set(comment.message);
  }

  protected cancelEdit(): void {
    this.editingId.set(null);
    this.editDraft.set('');
  }

  protected onEditInput(event: Event): void {
    this.editDraft.set((event.target as HTMLInputElement).value);
  }

  protected submitEdit(id: string): void {
    if (this.disabled()) return;
    const message = this.editDraft().trim();
    if (!message) return;
    this.editComment.emit({ id, message });
    this.cancelEdit();
  }

  protected timeValueForCopy(t: TimelineStepInfo): string {
    const lang = this.currentLang();
    return `${getFormatedTimeFromDate(t.startDate, lang)} – ${getFormatedTimeFromDate(t.endDate, lang)}`;
  }

  protected copyToClipboard(value: string): void {
    navigator.clipboard.writeText(value).then(
      () => {
        this.notificationService.success(
          this.translate.instant('table.cell.overlay_actions.copy.message_success'),
          '',
          2000
        );
      },
      () => {
        this.notificationService.error(
          this.translate.instant('table.cell.overlay_actions.copy.message_error'),
          ''
        );
      }
    );
  }

  /** Returns up to 2 uppercase initials from the first two whitespace-separated words. */
  protected initials(fullname: string): string {
    return fullname
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('');
  }
}
